import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { fetchAllRows } from "@/lib/fetchAllRows";
import { normalizeBacklogItem, type RawBacklogItem } from "@/lib/backlogUtils";
import type { BacklogItem } from "./useBacklogItems";

export const useDeletedBacklogItems = () => {
  return useQuery<BacklogItem[]>({
    queryKey: ["deleted-backlog-items"],
    queryFn: async () => {
      // Page through all rows — an unbounded select is capped at 1000.
      const data = await fetchAllRows((from, to) =>
        supabase
          .from("backlog_items")
          .select("*")
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false })
          // Tiebreaker for stable page boundaries (deleted_at can repeat).
          .order("id", { ascending: true })
          .range(from, to),
      );
      // Normalize rather than cast: assigned_to_id/subsheet_ids arrive in more
      // than one wire format, and consumers call .some()/.includes() on them.
      return data.map((row) => normalizeBacklogItem(row as unknown as RawBacklogItem));
    },
    staleTime: 5 * 60 * 1000, // 5 min — realtime handles freshness
    gcTime: 24 * 60 * 60 * 1000,
  });
};

export const useRestoreBacklogItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemIds: string[]) => {
      const { error } = await supabase.from("backlog_items").update({ deleted_at: null }).in("id", itemIds);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all relevant queries immediately
      queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      queryClient.refetchQueries({ queryKey: ["backlog-items"] });
    },
  });
};

export const usePermanentlyDeleteBacklogItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemIds: string[]) => {
      const { error } = await supabase.from("backlog_items").delete().in("id", itemIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
    },
  });
};
